import { ActionIcon, Group, Menu, Modal, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
	useCreateCustomProperty,
	useDeleteCustomProperty,
	useUpdateCustomProperty,
	type CreateCustomPropertyVariables,
} from '@repo/api-codegen';
import {
	useListCustomPropertiesV2,
	type CustomPropertyOut,
	type PagedCustomPropertyOut,
} from '@repo/api-codegen/v2';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, Icon } from '@repo/foundations';
import { memo, useCallback, useMemo, useState } from 'react';
import { queryClient, useQuestionsEnabled } from '../../api';
import type { FetchModelListHook } from '../../api/factories/types';
import {
	closeAllModals,
	openDeleteConfirmModal,
} from '../../components/ModalManager';
import TableV2 from '../../components/TableV2/TableV2';
import { v4 as uuidv4 } from '../../utils/uuid/v4';
import { useCustomPropertiesSettingsColumns } from './CustomPropertiesSettings.hooks';
import { CustomPropertiesForm } from './components/CustomPropertiesForm';
import {
	CUSTOM_PROPERTY_FORM_FIELDS,
	CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES,
} from './constants';
import type {
	CustomPropertyErrorHandler,
	CustomPropertyFormState,
	CustomPropertyFormType,
	CustomPropertySourceFilter,
	CustomPropertyWithBase,
} from './types';

export const CustomPropertiesSettingsController = memo(() => {
	const [sourceFilter, setSourceFilter] =
		useState<CustomPropertySourceFilter>('all');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [propertyToModify, setPropertyToModify] =
		useState<CustomPropertyOut | null>(null);

	const [formState, setFormState] = useState<CustomPropertyFormState>({
		label: '',
		type: '',
		visibility: [],
		errors: {},
	});

	const resetForm = useCallback(() => {
		setFormState({
			label: '',
			type: '',
			visibility: [],
			errors: {},
		});
	}, []);

	const formFields = useMemo(
		() => CUSTOM_PROPERTY_FORM_FIELDS as Array<'label' | 'type' | 'visibility'>,
		[]
	);

	const { data: allCustomProperties, queryKey } = useListCustomPropertiesV2({
		queryParams: {
			...(sourceFilter !== 'all' ? { source_filter: sourceFilter } : {}),
		},
	});
	const questionsEnabled = useQuestionsEnabled();

	const filteredEntityTypesCount = useMemo(
		() =>
			CUSTOM_PROPERTY_VISIBILITY_ENTITY_TYPES.filter(
				(entityType) => entityType !== EntityType.question || questionsEnabled
			).length,
		[questionsEnabled]
	);

	const existingLabels = useMemo(
		() =>
			allCustomProperties?.results
				? allCustomProperties.results.map((property) => property.name)
				: [],
		[allCustomProperties]
	);

	const onMutate = useCallback(
		async (variables: CreateCustomPropertyVariables) => {
			await queryClient.cancelQueries({ queryKey });

			const previousData =
				queryClient.getQueryData<PagedCustomPropertyOut>(queryKey);

			const optimisticProperty: CustomPropertyOut = {
				id: uuidv4(),
				name: variables.body.name,
				value_type: variables.body.value_type,
				entity_types: (variables.body.entity_types || []) as EntityType[],
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
				source: 'workspace',
			} as CustomPropertyOut;

			if (previousData) {
				const existingResults = Array.isArray(previousData.results)
					? previousData.results
					: [];
				queryClient.setQueryData<PagedCustomPropertyOut>(queryKey, {
					...previousData,
					results: [optimisticProperty, ...existingResults],
					count: (previousData.count ?? 0) + 1,
				});
			}

			return { previousData };
		},
		[queryKey]
	);

	const onError = useCallback<CustomPropertyErrorHandler>(
		(_error, _variables, context) => {
			if (context && typeof context === 'object' && 'previousData' in context) {
				queryClient.setQueryData(
					queryKey,
					(context as { previousData: PagedCustomPropertyOut | undefined })
						.previousData
				);
			}
			showNotification({
				title: 'Failed to create property',
				message:
					'There was an error creating the custom property. Please try again.',
				color: 'red',
			});
		},
		[queryKey]
	);

	const onSuccess = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey,
		});

		resetForm();
		setIsModalOpen(false);
	}, [queryKey, resetForm]);

	const handleCancel = useCallback(() => {
		setIsModalOpen(false);
		setPropertyToModify(null);
		resetForm();
	}, [resetForm]);

	const { mutateAsync: createCustomProperty, isPending } =
		useCreateCustomProperty({
			onMutate,
			onError,
			onSuccess,
		});

	const { mutateAsync: deleteCustomProperty } = useDeleteCustomProperty({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey,
			});
			closeAllModals();
		},
		onError: () => {
			closeAllModals();
			showNotification({
				title: 'Error',
				message: 'Failed to delete custom property. Please try again.',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		},
	});

	const { mutateAsync: updateCustomProperty, isPending: isUpdatePending } =
		useUpdateCustomProperty({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey,
				});
				handleCancel();
			},
			onError: () => {
				handleCancel();
				showNotification({
					title: 'Error',
					message: 'Failed to update custom property. Please try again.',
					color: 'red',
					icon: (
						<Icon name="alertCircle" color="text/critical-on-fill/default" />
					),
				});
			},
		});

	const handleConfirm = useCallback<CustomPropertyFormType['onConfirm']>(
		async (label, type, visibility) => {
			await createCustomProperty({
				body: {
					name: label,
					value_type: type,
					entity_types:
						visibility.length === filteredEntityTypesCount
							? [EntityType.all]
							: visibility,
				},
			});
		},
		[createCustomProperty, filteredEntityTypesCount]
	);

	const handleCreatePropertyClick = useCallback(() => {
		resetForm();
		setIsModalOpen(true);
	}, [resetForm]);

	const handleModifyPropertyClick = useCallback(
		(customProperty: CustomPropertyOut) => {
			setPropertyToModify(customProperty);

			const entityTypes = (customProperty.entity_types || []) as EntityType[];
			// Filter out question type if questions are disabled, to match form behavior
			const filteredEntityTypes = questionsEnabled
				? entityTypes
				: entityTypes.filter((type) => type !== EntityType.question);
			const visibility =
				entityTypes.includes(EntityType.all) ||
				filteredEntityTypes.length === filteredEntityTypesCount
					? [EntityType.all]
					: filteredEntityTypes;

			setFormState({
				label: customProperty.name,
				type: customProperty.value_type,
				visibility: visibility,
				errors: {},
			});
			setIsModalOpen(true);
		},
		[filteredEntityTypesCount, questionsEnabled]
	);

	const handleModifyConfirm = useCallback<CustomPropertyFormType['onConfirm']>(
		async (label, type, visibility) => {
			if (!propertyToModify) {
				return;
			}

			await updateCustomProperty({
				pathParams: { customPropertyId: propertyToModify.id },
				body: {
					name: label,
					value_type: type,
					entity_types:
						visibility.length === filteredEntityTypesCount
							? [EntityType.all]
							: visibility,
				},
			});
		},
		[updateCustomProperty, propertyToModify, filteredEntityTypesCount]
	);

	const handleDeletePropertyClick = useCallback(
		(customProperty: CustomPropertyOut) => {
			openDeleteConfirmModal({
				title: 'Delete custom property?',
				description: "You can't undo this action.",
				onConfirm: async () => {
					await deleteCustomProperty({
						pathParams: { customPropertyId: customProperty.id },
					});
				},
			});
		},
		[deleteCustomProperty]
	);

	const columns = useCustomPropertiesSettingsColumns({
		onDelete: handleDeletePropertyClick,
		onModify: handleModifyPropertyClick,
		questionsEnabled,
	});

	const usePaginationListOptions = useMemo(
		() =>
			sourceFilter !== 'all'
				? {
						queryParams: {
							source_filter: sourceFilter,
						},
					}
				: {},
		[sourceFilter]
	);

	const additionalButtons = useMemo(
		() => (
			<Group spacing="xs" noWrap>
				<Menu>
					<Menu.Target>
						<ActionIcon variant="tertiary" size="md">
							<Icon name="filter" />
						</ActionIcon>
					</Menu.Target>
					<Menu.Dropdown>
						<Menu.Item
							rightSection={sourceFilter === 'all' && <Icon name="check" />}
							onClick={() => setSourceFilter('all')}
						>
							All
						</Menu.Item>
						<Menu.Item
							rightSection={
								sourceFilter === 'integration' && <Icon name="check" />
							}
							onClick={() => setSourceFilter('integration')}
						>
							Created by integration
						</Menu.Item>
						<Menu.Item
							rightSection={
								sourceFilter === 'workspace' && <Icon name="check" />
							}
							onClick={() => setSourceFilter('workspace')}
						>
							Created in workspace
						</Menu.Item>
					</Menu.Dropdown>
				</Menu>
				<Group spacing="xs">
					<Button
						variant="primary"
						size="md"
						onClick={handleCreatePropertyClick}
					>
						Create property
					</Button>
				</Group>
			</Group>
		),
		[handleCreatePropertyClick, sourceFilter]
	);

	return (
		<Stack>
			<Modal
				opened={isModalOpen}
				onClose={handleCancel}
				title={propertyToModify ? 'Modify property' : 'Create property'}
			>
				<CustomPropertiesForm
					onCancel={handleCancel}
					onConfirm={propertyToModify ? handleModifyConfirm : handleConfirm}
					existingLabels={existingLabels}
					questionsEnabled={questionsEnabled}
					isIntegration={
						propertyToModify ? !!propertyToModify?.integration : false
					}
					originalLabel={propertyToModify ? propertyToModify.name : undefined}
					formState={formState}
					setFormState={setFormState}
					errors={formState.errors}
					setErrors={(errors) => setFormState((prev) => ({ ...prev, errors }))}
					fields={formFields}
					isPending={propertyToModify ? isUpdatePending : isPending}
				/>
			</Modal>

			<TableV2<CustomPropertyWithBase>
				pluralTypeString="custom properties"
				tableCacheKey="custom-properties-settings"
				key="custom-properties-settings-table"
				columns={columns}
				usePaginationList={
					useListCustomPropertiesV2 as FetchModelListHook<CustomPropertyWithBase>
				}
				useCodegenListInterface={true}
				usePaginationListOptions={usePaginationListOptions}
				withCheckbox={false}
				withSearch
				withDialog={false}
				withInfiniteScroll={false}
				defaultSort={null}
				noRecordsText="No custom properties found"
				paginationSize={1000}
				withAdditionalButtons={additionalButtons}
			/>
		</Stack>
	);
});
